export type DiffLineType = "add" | "remove" | "unchanged";

export interface DiffLine {
  type: DiffLineType;
  content: string;
  oldLineNumber?: number;
  newLineNumber?: number;
}

export interface WordDiff {
  type: "add" | "remove" | "unchanged";
  text: string;
}

export interface DiffHunk {
  id: number;
  oldStart: number;
  oldEnd: number;
  newStart: number;
  newEnd: number;
  lines: DiffLine[];
  status: "pending" | "accepted" | "rejected";
}

export interface DiffResult {
  lines: DiffLine[];
  hunks: DiffHunk[];
  stats: {
    additions: number;
    deletions: number;
    unchanged: number;
  };
}

function lcs(a: string[], b: string[]): number[][] {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  return dp;
}

export function computeLineDiff(oldContent: string, newContent: string): DiffResult {
  const oldLines = oldContent.split("\n");
  const newLines = newContent.split("\n");
  const dp = lcs(oldLines, newLines);

  const lines: DiffLine[] = [];
  let i = oldLines.length;
  let j = newLines.length;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
      lines.unshift({
        type: "unchanged",
        content: oldLines[i - 1],
        oldLineNumber: i,
        newLineNumber: j,
      });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      lines.unshift({ type: "add", content: newLines[j - 1], newLineNumber: j });
      j--;
    } else {
      lines.unshift({ type: "remove", content: oldLines[i - 1], oldLineNumber: i });
      i--;
    }
  }

  const hunks = buildHunks(lines);
  const stats = {
    additions: lines.filter((l) => l.type === "add").length,
    deletions: lines.filter((l) => l.type === "remove").length,
    unchanged: lines.filter((l) => l.type === "unchanged").length,
  };

  return { lines, hunks, stats };
}

function buildHunks(lines: DiffLine[], contextLines = 3): DiffHunk[] {
  const hunks: DiffHunk[] = [];
  let currentHunkLines: DiffLine[] = [];
  let hunkStart = -1;
  let hunkId = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.type !== "unchanged") {
      if (currentHunkLines.length === 0) {
        const contextStart = Math.max(0, i - contextLines);
        for (let c = contextStart; c < i; c++) {
          currentHunkLines.push(lines[c]);
        }
        hunkStart = contextStart;
      }
      currentHunkLines.push(line);
    } else if (currentHunkLines.length > 0) {
      currentHunkLines.push(line);
      const remainingChanges = lines.slice(i + 1).findIndex((l) => l.type !== "unchanged");
      if (remainingChanges === -1 || remainingChanges > contextLines * 2) {
        const contextEnd = Math.min(i + contextLines, lines.length - 1);
        for (let c = i + 1; c <= contextEnd && lines[c]?.type === "unchanged"; c++) {
          currentHunkLines.push(lines[c]);
        }
        hunks.push({
          id: hunkId++,
          oldStart: lines[hunkStart]?.oldLineNumber || 1,
          oldEnd: currentHunkLines[currentHunkLines.length - 1]?.oldLineNumber || 1,
          newStart: lines[hunkStart]?.newLineNumber || 1,
          newEnd: currentHunkLines[currentHunkLines.length - 1]?.newLineNumber || 1,
          lines: [...currentHunkLines],
          status: "pending",
        });
        currentHunkLines = [];
        hunkStart = -1;
      }
    }
  }

  if (currentHunkLines.length > 0) {
    hunks.push({
      id: hunkId++,
      oldStart: lines[hunkStart]?.oldLineNumber || 1,
      oldEnd: currentHunkLines[currentHunkLines.length - 1]?.oldLineNumber || 1,
      newStart: lines[hunkStart]?.newLineNumber || 1,
      newEnd: currentHunkLines[currentHunkLines.length - 1]?.newLineNumber || 1,
      lines: currentHunkLines,
      status: "pending",
    });
  }

  return hunks;
}

export function computeWordDiff(oldText: string, newText: string): WordDiff[] {
  const oldWords = tokenize(oldText);
  const newWords = tokenize(newText);
  const dp = lcs(oldWords, newWords);

  const result: WordDiff[] = [];
  let i = oldWords.length;
  let j = newWords.length;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldWords[i - 1] === newWords[j - 1]) {
      result.unshift({ type: "unchanged", text: oldWords[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.unshift({ type: "add", text: newWords[j - 1] });
      j--;
    } else {
      result.unshift({ type: "remove", text: oldWords[i - 1] });
      i--;
    }
  }

  return result;
}

function tokenize(text: string): string[] {
  return text.match(/\S+|\s+/g) || [];
}
